import * as React from 'react';
import * as _ from 'underscore';

import { BodyCell, DataGrid } from '@tableau/data-grid-3-wrapper';
import { Messages } from '../../../codegen/Localize';
import { CommonStyles } from '../../../common/react/CommonStyles';
import { IReactTranslateService } from '../../../common/react/IReactTranslateService';
import { cssClass, textEllipsis } from '../../../style';
import { L10n } from '../../../ts/L10n/L10n';
import { INamedItemInfo } from '../../../ts/models/resourceInfoParts';
import { RunFlowTaskInfo } from '../../../ts/models/RunFlowTaskInfo';
import { ScheduleActions, ScheduleTypes } from '../../../ts/ServerApi/types/ISchedule';
import { DataGridCells } from '../ListView/DataGridCells';
import { StyledCellFormatter } from '../ListView/StyledCellWrapper';

export namespace SchedulesGridCells {

  export function FrequencyCell(): React.StatelessComponent<BodyCell.Props> {
    const scheduleTypeToMessageKey: _.Dictionary<string> = {};
    scheduleTypeToMessageKey[ScheduleTypes.Daily] = Messages.daily();
    scheduleTypeToMessageKey[ScheduleTypes.Hourly] = Messages.hourly();
    scheduleTypeToMessageKey[ScheduleTypes.Monthly] = Messages.monthly();
    scheduleTypeToMessageKey[ScheduleTypes.Weekly] = Messages.weekly();

    return DataGridCells.PlainTextCellWithTransform('scheduleType',
      (scheduleType: string) => scheduleTypeToMessageKey[scheduleType]
    );
  }

  export function TaskTypeCell(): React.StatelessComponent<BodyCell.Props> {
    const scheduleActionToMessageKey: _.Dictionary<string> = {};
    scheduleActionToMessageKey[ScheduleActions.Extract] = Messages.extractRefresh();
    scheduleActionToMessageKey[ScheduleActions.Flow] = Messages.flow();
    scheduleActionToMessageKey[ScheduleActions.Subscription] = Messages.subscription();
    scheduleActionToMessageKey[ScheduleActions.Data_Acceleration] = Messages.acceleration();

    return DataGridCells.PlainTextCellWithTransform('scheduledAction',
      (scheduledAction: string) => scheduleActionToMessageKey[scheduledAction]
    );
  }

  export function ExecutionCell(): React.StatelessComponent<BodyCell.Props> {
    return DataGridCells.PlainTextCellWithTransform('parallel',
      (parallel: boolean) => parallel ? Messages.scheduleBehaviorParallel() : Messages.scheduleBehaviorSerial()
    );
  }

  function getOutputStepNamesForRow(row: DataGrid.Row): ReadonlyArray<INamedItemInfo> {
    const runFlowTaskInfo = row.data as RunFlowTaskInfo;
    if (runFlowTaskInfo && runFlowTaskInfo.flowOutputSteps) {
      return runFlowTaskInfo.flowOutputSteps;
    }
    return [];
  }

  export function ServerOutputsCell(translate: IReactTranslateService) {
    return StyledCellFormatter(itemsCellWithPopover(getOutputStepNamesForRow, 'outputStepsCount', translate));
  }

  export function itemsCellWithPopover(
    getItemsForRow: (row: DataGrid.Row) => ReadonlyArray<INamedItemInfo>,
    popoverTitleLocKey: string,
    translate: IReactTranslateService,
  ): React.StatelessComponent<BodyCell.Props> {
    return function ItemsCellWithPopover(props: BodyCell.Props): JSX.Element {
      const items = getItemsForRow(props.row);
      const itemCount = items.length;
      const firstItemName = itemCount > 0 ? items.find(item => item !== undefined).name : '';
      return DataGridCells.PlainTextCellWithCount(
        firstItemName,
        getListPopoverContent(getItemsForRow, popoverTitleLocKey, translate),
        additionalItemsText(itemCount, translate)
      )(props);
    };
  }

  const cssPrefix = 'ItemsCellPopover';

  const popoverRows = cssClass(cssPrefix, 'rows', {
    marginTop: CommonStyles.Sizes.DefaultSpace
  });

  const popoverRow = cssClass(cssPrefix, 'row', textEllipsis, {
    height: CommonStyles.Sizes.CompactRowHeight,
    lineHeight: CommonStyles.Sizes.CompactRowHeight + 'px',
    cursor: 'default'
  });

  function getListPopoverContent(
    getItemsForRow: (row: DataGrid.Row) => ReadonlyArray<INamedItemInfo>,
    popoverTitleLocKey: string,
    translate: IReactTranslateService,
  ): (props: BodyCell.Props) => Promise<JSX.Element> {
    return (props: BodyCell.Props) => {
      const items = getItemsForRow(props.row);
      const titleString = translate(popoverTitleLocKey, {
        COUNT: items.length
      }, L10n.Interpolations.Count);
      return Promise.resolve(
        <div data-tb-test-id="popover-menu-panel">
          <div className="tb-content-item-popover-title">{titleString}</div>
          <div className={popoverRows}>
            {_.map(items, listPopoverRow)}
          </div>
        </div>
      );
    };
  }

  function listPopoverRow(item: INamedItemInfo): JSX.Element {
    return (
      <div className={popoverRow} key={item.id}>
        <span title={item.name}>{item.name}</span>
      </div>
    );
  }

  function additionalItemsText(itemCount: number, translate: IReactTranslateService): string {
    if (itemCount === 0 || itemCount === 1) {
      return '';
    }
    return translate('additionalItemsPillText', {
      ADDITIONAL_COUNT_TEXT: L10n.localizedCount(itemCount - 1)
    });
  }
}
